"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { getDefaultQueryRetry, WAVE_LOGS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiWaveLog } from "@/generated/models/ApiWaveLog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
interface UseWaveActivityLogsProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | undefined;
  readonly reverse: boolean;
  readonly dropId: string | null;
  readonly logTypes: string[];
}
export function useWaveActivityLogs(t0) {
  const $ = _c(32);
  const {
    waveId,
    connectedProfileHandle,
    reverse,
    dropId,
    logTypes
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [logs, setLogs] = useState(t1);
  let t2;
  if ($[1] !== dropId || $[2] !== logTypes || $[3] !== waveId) {
    t2 = [QueryKey.WAVE_LOGS, {
      waveId,
      limit: WAVE_LOGS_PARAMS.limit,
      dropId,
      logTypes
    }];
    $[1] = dropId;
    $[2] = logTypes;
    $[3] = waveId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const queryKey = t2;
  let t3;
  if ($[5] !== dropId || $[6] !== logTypes || $[7] !== queryClient || $[8] !== queryKey || $[9] !== waveId) {
    t3 = () => {
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t4 => {
          const {
            pageParam
          } = t4;
          const params = {
            limit: WAVE_LOGS_PARAMS.limit.toString()
          };
          if (dropId) {
            params.drop_id = dropId;
          }
          if (logTypes) {
            params.log_types = logTypes.join(",");
          }
          if (pageParam) {
            params.offset = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/logs`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp,
        pages: 3,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    $[5] = dropId;
    $[6] = logTypes;
    $[7] = queryClient;
    $[8] = queryKey;
    $[9] = waveId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== waveId) {
    t4 = [waveId];
    $[11] = waveId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== dropId || $[14] !== logTypes || $[15] !== waveId) {
    t5 = async t6 => {
      const {
        pageParam: pageParam_0
      } = t6;
      const params_0 = {
        limit: WAVE_LOGS_PARAMS.limit.toString()
      };
      if (dropId) {
        params_0.drop_id = dropId;
      }
      if (logTypes) {
        params_0.log_types = logTypes.join(",");
      }
      if (pageParam_0 !== null) {
        params_0.offset = `${pageParam_0}`;
      }
      const results = await commonApiFetch({
        endpoint: `waves/${waveId}/logs`,
        params: params_0
      });
      return results;
    };
    $[13] = dropId;
    $[14] = logTypes;
    $[15] = waveId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = !!connectedProfileHandle;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = getDefaultQueryRetry();
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== queryKey || $[19] !== t5 || $[20] !== t6) {
    t8 = {
      queryKey,
      queryFn: t5,
      initialPageParam: 0,
      getNextPageParam: _temp2,
      placeholderData: keepPreviousData,
      enabled: t6,
      staleTime: 60000,
      refetchInterval: 30000,
      ...t7
    };
    $[18] = queryKey;
    $[19] = t5;
    $[20] = t6;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage
  } = useInfiniteQuery(t8);
  let t10;
  let t9;
  if ($[22] !== data || $[23] !== reverse) {
    t9 = () => {
      setLogs(() => {
        const newLogs = data?.pages ? data.pages.flat() : [];
        return reverse ? newLogs.reverse() : newLogs;
      });
    };
    t10 = [data, reverse];
    $[22] = data;
    $[23] = reverse;
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] !== fetchNextPage || $[27] !== hasNextPage || $[28] !== isFetchingNextPage || $[29] !== isLoading || $[30] !== logs) {
    t11 = {
      logs,
      fetchNextPage,
      hasNextPage,
      isLoading,
      isFetchingNextPage
    };
    $[26] = fetchNextPage;
    $[27] = hasNextPage;
    $[28] = isFetchingNextPage;
    $[29] = isLoading;
    $[30] = logs;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp2(lastPage_0, allPages_0) {
  return lastPage_0.length === WAVE_LOGS_PARAMS.limit ? allPages_0.length * WAVE_LOGS_PARAMS.limit : null;
}
function _temp(lastPage, allPages) {
  return lastPage.length === WAVE_LOGS_PARAMS.limit ? allPages.length * WAVE_LOGS_PARAMS.limit : null;
}